import * as React from 'react';
import ConfirmPopup from './ConfirmPopup';
import AddPriceListTier from './AdminPriceListsAddTierModal';
import { toast } from 'react-toastify';
import type { GenericList, ItemOptionList } from './models/JobSharedInterfaces';
import type { PriceListTierList } from './models/AdminInterfaces';
import { useNavigate } from 'react-router-dom';
import { CheckPermissions, GetQuery, PostQuery } from './Shared';

interface PriceListSetWeeksItem {
    id: number;
    tierId: number;
    customerId: number;
    itemId: number;
    item: string;
    itemTypeId: number;
    frequencyId: number;
    oneWeek: number;
    sixWeek: number;
    tenWeek: number;
    thirteenWeek: number;
    fourteenWeek: number;
    twentyWeek: number;
    twentySixWeek: number;
    fortyWeek: number;
    fiftyTwoWeek: number;
    additionalHireWeeklyRate: number;
    travelOneWayRate: number;
    statusId: number;
    isDirty: boolean;
}

const AdminPriceListSetWeeksData = () => {
    let navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [pricelistLoading, setPricelistLoading] = React.useState(false);
    const [priceListTiers, setPriceListTiers] = React.useState<PriceListTierList[]>([]);
    const [priceListTier, setPriceListTier] = React.useState({ priceListUniqueId: "", priceListTierId: -1, priceListTierType: -1 });
    const [itemList, setItemList] = React.useState<ItemOptionList[]>([]);
    const [frequencyList, setFrequencyList] = React.useState<GenericList[]>([]);
    const [priceListItems, setPriceListItems] = React.useState<PriceListSetWeeksItem[]>([]);
    const [useOverhireRate, setUseOverhireRate] = React.useState(true);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
    const [deleteIndex, setDeleteIndex] = React.useState(-1);
    const [showAddTier, setShowAddTier] = React.useState(false);
    const [readOnly, setReadOnly] = React.useState(false);

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        var hasPermission = await CheckPermissions("AdminPricelistsView");
        if (hasPermission) {
            getPriceListData();
        } else {
            navigate("/accessdenied");
        }
    }

    const getPriceListData = async () => {
        const apiUrl = "GetPriceListData?IsEws=false&IsPumpClean=false&IsEpa=false&IsFencing=false&IsEvent=false";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setItemList(result.data.itemList);
            var frequency: GenericList[] = result.data.frequencyList;
            frequency = frequency.filter(f => f.id < 4);
            setFrequencyList(frequency);
            setPriceListTiers(result.data.priceListTier);
            setReadOnly(result.data.readOnly);
            setLoading(false);
        }
    }

    const getPricelist = async (id: number, typeId: number) => {
        if (id > 0) {
            setPricelistLoading(true);

            const apiUrl = "GetPriceListSetWeeks?Id=" + id + '&PriceListType=' + typeId;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                var pricelist = result.data.priceList;
                let pricelistItem = addBlankRow(id, typeId * 1);
                pricelist.push(pricelistItem);

                setPriceListItems(pricelist);
                setUseOverhireRate(result.data.useOverhireRate);
                setPricelistLoading(false);
            }
        } else {
            setPriceListItems([]);
            setPricelistLoading(false);
        }
    }

    const addBlankRow = (id: number, typeId: number) => {
        var tierId = -1;
        var customerId = -1;
        if (typeId === 1) {
            tierId = id;
        } else {
            customerId = id;
        }

        let pricelistItem: PriceListSetWeeksItem = {
            id: 0,
            tierId: tierId,
            customerId: customerId,
            itemId: -1,
            item: "",
            itemTypeId: -1,
            frequencyId: -1,
            oneWeek: 0,
            sixWeek: 0,
            tenWeek: 0,
            thirteenWeek: 0,
            fourteenWeek: 0,
            twentyWeek: 0,
            twentySixWeek: 0,
            fortyWeek: 0,
            fiftyTwoWeek: 0,
            additionalHireWeeklyRate: 0,
            travelOneWayRate: 0,
            statusId: 1,
            isDirty: false
        };

        return pricelistItem;
    }

    const handlePriceListChange = (e: any) => {
        var uniqueId = e.target.value;
        var plId = uniqueId.substring(1) * 1;
        let typeId = 1;

        if (uniqueId != "") {
            let priceListOption = priceListTiers[e.target.selectedIndex - 1];
            if (priceListOption) {
                typeId = priceListOption.typeId;
            }
        }

        setPriceListTier({ priceListUniqueId: uniqueId, priceListTierId: plId, priceListTierType: typeId });

        if (uniqueId != "") {
            getPricelist(plId, typeId);
        } 
    }

    const clearErrors = (errorName: string) => {
        let errorList = errors;
        if (errorName) {
            delete errorList[errorName];
            setErrors(errorList);
        }
    }

    const handleCellChange = (e: any, itemIndex: number) => {
        clearErrors("pricelistitems");

        setPriceListItems(prevState => prevState.map((item, index) =>
            index == itemIndex ? { ...item, [e.target.name]: Number(e.target.value), isDirty: true } : item
        ));

        if (itemIndex == (priceListItems.length - 1)) {
            //add new row
            var newItem = addBlankRow(priceListTier.priceListTierId, priceListTier.priceListTierType);
            setPriceListItems(prevList => [...prevList, newItem]);
        }
    }

    const handleCellItemIdChange = (e: any, itemIndex: number) => {
        clearErrors("pricelistitems");

        var newValue = Number(e.target.value);
        //get item type id
        var item = itemList.find(i => i.id === newValue);
        if (item) {
            setPriceListItems(prevState => prevState.map((item, index) =>
                index == itemIndex ? { ...item, [e.target.name]: newValue, itemTypeId: item.itemTypeId, item: item.item, isDirty: true } : item
            ));
        }

        if (itemIndex == (priceListItems.length - 1)) {
            //add new row
            var newItem = addBlankRow(priceListTier.priceListTierId, priceListTier.priceListTierType);
            setPriceListItems(prevList => [...prevList, newItem]);
        }
    }

    const handleUseOverhireRateCheckboxChange = (e: any) => {
        updateUseOverhireRate(e.target.checked);
    }

    const updateUseOverhireRate = async (useOverhireRate: boolean) => {
        var saveUseOverhire = {
            id: priceListTier.priceListTierId,
            typeId: priceListTier.priceListTierType,
            useOverhireRate: useOverhireRate
        };

        var jsonItem = JSON.stringify(saveUseOverhire);

        const apiUrl = "SaveTierUseOverhireRate";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Use Overhire Rate Saved Successfully");
                setUseOverhireRate(useOverhireRate);
            }
        }
    }

    //ADD TIER
    const addTier = () => {
        setShowAddTier(true);
    }

    const hideAddTier = () => {
        setShowAddTier(false);
    }

    const createNewBlankPriceListItem = (id: number, typeId: number) => {
        let pricelist: PriceListSetWeeksItem[] = [];
        let pricelistItem = addBlankRow(id, typeId);
        pricelist.push(pricelistItem);
        setPriceListItems(pricelist);
    }

    const saveAddTier = (customerTierId: number, name: string, typeId: number) => {
        //need to add the customer to the dropdown
        var uniqueType = typeId === 1 ? 'T' : 'C';
        var uniqueId = uniqueType + customerTierId;
        var newItem: PriceListTierList = {
            id: customerTierId,
            uniqueId: uniqueId,
            name: name,
            typeId: typeId
        };
        setPriceListTiers(prevList => [...prevList, newItem]);
        setPriceListTier(prevState => ({ ...prevState, priceListUniqueId: uniqueId, priceListTierId: customerTierId, priceListTierType: typeId }));
        createNewBlankPriceListItem(customerTierId, typeId);
        setUseOverhireRate(true);
        setShowAddTier(false);
    }
    //END ADD TIER

    //DELETE PRICELIST ITEM
    const deleteItem = (e: any, itemIndex: number) => {
        e.preventDefault();

        setShowDeleteConfirm(true);
        setDeleteIndex(itemIndex);
    }

    const hideDeleteConfirm = () => {
        setShowDeleteConfirm(false);
        setDeleteIndex(-1);
    }

    const saveDeleteConfirm = () => {
        var pricelist = [...priceListItems];
        var pricelistItem = pricelist[deleteIndex];

        if (pricelistItem.id === 0) {
            //just remove from the list as not saved
            pricelist.splice(deleteIndex, 1);
            setShowDeleteConfirm(false);
            setDeleteIndex(-1);
            setPriceListItems(pricelist);

            toast.success("Item deleted successfully");
        } else {
            saveDeletePricelist(pricelist, pricelistItem.id);
        }
    }

    const saveDeletePricelist = async (pricelist: PriceListSetWeeksItem[], pricelistItemId: number) => {
        var deletePriceList = {
            id: pricelistItemId
        };

        var jsonItem = JSON.stringify(deletePriceList);

        const apiUrl = "DeletePricelistSetWeeksItem";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Price List Item Deleted Successfully");
                pricelist.splice(deleteIndex, 1);
                setShowDeleteConfirm(false);
                setDeleteIndex(-1);
                setPriceListItems(pricelist);
            }
        }
    }
    //END DELETE PRICELIST ITEM

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;
        let items = priceListItems;

        if (priceListItems.length === 1) {
            errorList["pricelistitems"] = "Please enter at least one item!";
            setErrors(errorList)
            return true;
        }

        var rowNumber = 0;
        var errorString = "Please enter the following values: ";
        var error = false;

        for (var i = 0; i < items.length - 1; i++) {
            rowNumber += 1;
            var newErrorString = "";

            if (items[i].itemId < 1) {
                newErrorString += "Item, ";
                error = true;
            }

            //must have at least one price entered
            var checkPrices = items[i].oneWeek + items[i].sixWeek + items[i].tenWeek + items[i].thirteenWeek + items[i].fourteenWeek + items[i].twentyWeek + items[i].twentySixWeek + items[i].fortyWeek + items[i].fiftyTwoWeek; 
            if (checkPrices <= 0) {
                newErrorString += "At least one weekly rate price(1, 6, 10, 13, 14, 20, 26, 40 or 52), ";
                error = true;
            }

            if (!items[i].additionalHireWeeklyRate || items[i].additionalHireWeeklyRate <= 0) {
                newErrorString += "Additional Hire Rate, ";
                error = true;
            }

            if (newErrorString != "") {
                errorString += "\n"
                errorString += "Row " + rowNumber + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }

        }
        if (error) {
            formIsValid = false;
            errorList["pricelistitems"] = errorString;
        }
        setErrors(errorList);
        return formIsValid;
    }

    const save = (e: any) => {
        e.preventDefault();

        if (validate()) {
            var changed = priceListItems.filter(p => p.isDirty);

            if (changed.length === 0) {
                toast.info("No items have changed.");
            } else {
                savePricelist(changed);
            }
        } else {
            toast.error("Please fix validation errors before Saving")
        }
    }

    const savePricelist = async (changed: PriceListSetWeeksItem[]) => {
        var savePriceList = {
            priceListItems: changed
        };

        var jsonItem = JSON.stringify(savePriceList);

        const apiUrl = "SavePriceListSetWeeks";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Price List Saved Successfully");
                setPricelistLoading(true);
                getPricelist(priceListTier.priceListTierId, priceListTier.priceListTierType);
            }
        }
    }

    const addNewTier = <AddPriceListTier showModal={showAddTier} systemCompanyId={3} hideModal={hideAddTier} saveModal={saveAddTier} />
    const lastRow = priceListItems.length - 1;
    const confirmPopup = <ConfirmPopup heading="Delete Price List Item?" text="Are you sure you want to delete this price list item?" hideConfirmModal={hideDeleteConfirm} showConfirmModal={showDeleteConfirm} saveConfirmModal={saveDeleteConfirm} />

    const renderDetails = (
        <div>
            <div className="static-modal">
                {confirmPopup}
                {addNewTier}
            </div>
            <div className="input-group-parent">
                <label className="input-group" htmlFor="priceListTierId">
                    <span className="label">Tier/Customer</span>
                    <select className="select marginRight10" id="priceListUniqueId" name="priceListUniqueId" value={priceListTier.priceListUniqueId} onChange={(e) => handlePriceListChange(e)}>
                        <option hidden defaultValue=""></option>
                        {priceListTiers.map(tier =>
                            <option key={tier.uniqueId} value={tier.uniqueId}>{tier.name}</option>
                        )};
                    </select>
                    <button className={readOnly ? "hidden" : "defaultbutton defaultbutton__small"} type="button" onClick={addTier}>Add New</button>
                </label>
            </div>
            <div className={priceListTier.priceListTierId > 0 && priceListTier.priceListTierType === 1 ? "input-group-parent" : "hidden"}>
                <label className="input-group" htmlFor="useOverhireRate">
                    <span className="label label-large label--nopadding">Use Pricelist Overhire Rate for Invoices</span>
                    <input className="checkbox" type="checkbox" id="useOverhireRate" name="useOverhireRate" checked={useOverhireRate} onChange={(e) => handleUseOverhireRateCheckboxChange(e)} disabled></input>
                </label>
            </div>

            <div>
                <hr></hr>
            </div>
            <div className={priceListTier.priceListTierId > 0 && !pricelistLoading ? "" : "hidden"}>
                <div className="overflowAuto">
                    <table className="table tableColours">
                        <thead>
                            <tr>
                                <th className="hidden">Id</th>
                                <th></th>
                                <th>Item</th>
                                <th>Maintenance</th>
                                <th>1 Week</th>
                                <th>6 Weeks</th>
                                <th>10 Weeks</th>
                                <th>13 Weeks</th>
                                <th>14 Weeks</th>
                                <th>20 Weeks</th>
                                <th>26 Weeks</th>
                                <th>40 Weeks</th>
                                <th>52 Weeks</th>
                                <th>Additional Weekly Rate</th>
                                <th>Travel One Way</th>
                                <th></th>
                            </tr>
                        </thead>
                        {priceListItems.map((item, index) =>
                            <tbody key={index}>
                                <tr>
                                    <td className="hidden">{item.id}</td>
                                    <td className="table__text--align">{index + 1}</td>
                                    <td className="table__text--align">
                                        <select className="select" id="itemId" name="itemId" value={item.itemId} onChange={(e) => handleCellItemIdChange(e, index)}>
                                            <option defaultValue="-1"></option>
                                            {itemList.map(item =>
                                                <option key={item.id} value={item.id}>{item.name}</option>
                                            )};
                                        </select>
                                    </td>
                                    <td className="table__text--align">
                                        <select className={item.itemTypeId === 25 && !item.item.toLowerCase().includes("sewer connect") ? "select" : "hidden"} id="frequencyId" name="frequencyId" value={item.frequencyId} onChange={(e) => handleCellChange(e, index)}>
                                            <option defaultValue="-1"></option>
                                            {frequencyList.map(item =>
                                                <option key={item.id} value={item.id}>{item.name}</option>
                                            )};
                                        </select>
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className='form-control textalignright' id="oneWeek" name="oneWeek" value={item.oneWeek} onChange={(e) => handleCellChange(e, index)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className='form-control textalignright' id="sixWeek" name="sixWeek" value={item.sixWeek} onChange={(e) => handleCellChange(e, index)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className='form-control textalignright' id="tenWeek" name="tenWeek" value={item.tenWeek} onChange={(e) => handleCellChange(e, index)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className='form-control textalignright' id="thirteenWeek" name="thirteenWeek" value={item.thirteenWeek} onChange={(e) => handleCellChange(e, index)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className='form-control textalignright' id="fourteenWeek" name="fourteenWeek" value={item.fourteenWeek} onChange={(e) => handleCellChange(e, index)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className='form-control textalignright' id="twentyWeek" name="twentyWeek" value={item.twentyWeek} onChange={(e) => handleCellChange(e, index)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className='form-control textalignright' id="twentySixWeek" name="twentySixWeek" value={item.twentySixWeek} onChange={(e) => handleCellChange(e, index)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className='form-control textalignright' id="fortyWeek" name="fortyWeek" value={item.fortyWeek} onChange={(e) => handleCellChange(e, index)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className='form-control textalignright' id="fiftyTwoWeek" name="fiftyTwoWeek" value={item.fiftyTwoWeek} onChange={(e) => handleCellChange(e, index)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className='form-control textalignright' id="additionalHireWeeklyRate" name="additionalHireWeeklyRate" value={item.additionalHireWeeklyRate} onChange={(e) => handleCellChange(e, index)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className='form-control textalignright' id="travelOneWayRate" name="travelOneWayRate" value={item.travelOneWayRate} onChange={(e) => handleCellChange(e, index)} />
                                    </td>
                                    <td className="table__text--align">
                                        <div className={index === lastRow ? "hidden" : "delete--tablecell"}>
                                            <a className="makeitred" href="#" onClick={(e) => deleteItem(e, index)}>
                                                <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                            </a>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        )}
                    </table>
                    {errors["pricelistitems"] ?
                        (errors["pricelistitems"]).split("\n").map((item: any, key: any) => {
                            return <span className="label errors" key={key}>{item}<br /></span>
                        })
                        : ""}
                </div>
            </div>
            <div className="colAlignRight marginBottom10">
                <button className={readOnly ? "hidden" : "defaultbutton defaultbutton__large"} type="button" onClick={save} disabled={priceListTier.priceListTierId < 0 || pricelistLoading ? true : false}>Save</button>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            <h1>Price List Set Weeks</h1>
            {contents}
        </div>
    );
}

export default AdminPriceListSetWeeksData;
